import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Clock } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { ScriptDraft } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';

export default function CorporateScripts() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<ScriptDraft[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    try {
      const response = await api.get('/scripts');
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to load scripts:', error);
    }
  };

  const filteredScripts = scripts.filter((script) => {
    const matchesSearch = 
      script.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      script.invitation?.kolProfile?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || script.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingReview = scripts.filter(s => s.status === 'SUBMITTED_FOR_BRAND');

  const statusOptions = [
    { value: 'ALL', label: 'All Status' },
    { value: 'SUBMITTED_FOR_BRAND', label: 'Pending Review' },
    { value: 'APPROVED_BY_BRAND', label: 'Approved' },
    { value: 'REVISION_REQUESTED', label: 'Revision Requested' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED_FOR_BRAND':
        return <Badge variant="warning">Pending Review</Badge>;
      case 'APPROVED_BY_BRAND':
        return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED':
        return <Badge variant="destructive">Revision Requested</Badge>;
      case 'DRAFT':
        return <Badge variant="secondary">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-title">Scripts</h1>
          <p className="text-glass-text-2 text-sm mt-1">Review and approve content scripts</p>
        </div>
      </div>

      {/* Stats */}
      {pendingReview.length > 0 && (
        <Card className="border-[rgb(var(--warn)/0.35)] bg-[rgb(var(--warn)/0.1)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">
                  {pendingReview.length} script{pendingReview.length !== 1 ? 's' : ''} waiting for your review
                </p>
                <p className="text-sm text-orange-700">Please review and provide feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by campaign or KOL name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Scripts List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {filteredScripts.length} Script{filteredScripts.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredScripts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'No scripts match your filters'
                  : 'No scripts submitted yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredScripts.map((script) => (
                <div
                  key={script.id}
                  onClick={() => navigate(`/brand/scripts/${toSlug(script.job?.title || 'script', script.id)}`)}
                  className="flex items-center justify-between p-4 border border-glass rounded-lg hover:bg-glass-3/40 cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {script.job?.title || 'Untitled Campaign'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          By {script.invitation?.kolProfile?.name || 'Unknown KOL'} • Version {script.version}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {script.reviewedBy && typeof script.reviewedBy === 'object' && (
                            <>Reviewed by {script.reviewedBy.fullName || script.reviewedBy.email} • </>
                          )}
                          Updated {new Date(script.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(script.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

